import { cn } from '@sparkai/utils'
import {
  ComponentPropsWithoutRef,
  ComponentRef,
  ElementType,
  ForwardedRef,
  forwardRef,
  ReactElement,
  RefAttributes,
} from 'react'

import { Icon, IconsName } from '../../Icon'

export const AvailableIconButtonSizes = ['xs', 'sm', 'base', 'lg', 'xl'] as const
export type IconButtonSize = (typeof AvailableIconButtonSizes)[number]

export const AvailableIconButtonShapes = ['square', 'circular'] as const
export type IconButtonShape = (typeof AvailableIconButtonShapes)[number]

export const AvailableIconButtonVariants = ['primary', 'white'] as const
export type IconButtonVariant = (typeof AvailableIconButtonVariants)[number]

export interface IconButtonBaseProps {
  shape?: IconButtonShape
  size?: IconButtonSize
  variant?: IconButtonVariant
  iconName: IconsName
}

export type IconButtonProps<TElement extends ElementType> = ComponentPropsWithoutRef<TElement> &
  IconButtonBaseProps & {
    as?: TElement
    iconProps?: Omit<ComponentPropsWithoutRef<typeof Icon>, 'name'>
  }

const IconButtonComponent = <TElement extends ElementType>(
  {
    as,
    size = 'base',
    variant = 'primary',
    shape = 'circular',
    iconName,
    className: classNameFromProps,
    iconProps,
    ...props
  }: IconButtonProps<TElement>,
  ref: ForwardedRef<ComponentRef<TElement>>
) => {
  const Element = as ?? 'button'

  return (
    <Element
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ref={ref as any}
      type='button'
      className={cn(
        className.base,
        className.shape[shape],
        className.sizes[size],
        className.variants[variant],
        classNameFromProps
      )}
      {...props}
    >
      <Icon name={iconName} {...iconProps} className={cn(className.icon[size], iconProps?.className)} />
    </Element>
  )
}

// Using type cast to make it possible to have generics even when using forwardRef
export const IconButton = forwardRef(IconButtonComponent) as <TElement extends ElementType = 'button'>(
  p: IconButtonProps<TElement> & RefAttributes<ComponentRef<TElement>>
) => ReactElement

const className = {
  base: 'inline-flex items-center justify-center disabled:cursor-not-allowed font-inter focus:ring-2 box-border focus:ring-brand-blue-500 focus:ring-offset-2 disabled:opacity-50 shadow-sm',
  shape: {
    square: 'rounded-md',
    circular: 'rounded-full',
  },
  icon: {
    xs: 'w-4 h-4',
    sm: 'w-4 h-4',
    base: 'w-5 h-5',
    lg: 'w-5 h-5',
    xl: 'w-5 h-5',
  },
  sizes: {
    xs: 'h-8 w-8',
    sm: 'h-9 w-9',
    base: 'w-10 h-10',
    lg: 'w-11 h-11',
    xl: 'w-12 h-12',
  },
  variants: {
    primary:
      'border-transparent text-white bg-brand-blue-500 hover:bg-brand-blue-600 disabled:bg-brand-blue-500',
    white:
      'border border-brand-gray-300 bg-white text-brand-gray-500 hover:bg-brand-blue-50 disabled:bg-white',
  },
}
